import { useEffect } from 'react';
import { useGetSettingsQuery } from './api/api';
import {
  IGalleryDesign,
  classifyFont,
  googleFontUrl,
  googleFontCssValue,
} from '@topshots/core-models';

function resolveFont(fontValue: string): string {
  const classification = classifyFont(fontValue);
  if (classification.type === 'google') {
    const { entry } = classification;
    const linkId = `gallery-font-${entry.name.replace(/\s/g, '-')}`;
    if (!document.getElementById(linkId)) {
      const link = document.createElement('link');
      link.id = linkId;
      link.rel = 'stylesheet';
      link.href = googleFontUrl(entry.name);
      document.head.appendChild(link);
    }
    return googleFontCssValue(entry);
  }
  return fontValue;
}

const themeMap: Record<keyof Omit<IGalleryDesign, 'font'>, string[]> = {
  backgroundColour: ['--color-background-primary'],
  textColour: ['--color-text', '--color-nav-text'],
  secondaryTextColour: ['--color-text-subdued'],
  tertiaryTextColour: ['--color-text-very-subdued'],
  highlightColour: [
    '--color-button-primary-bg',
    '--color-button-primary-border',
    '--color-text-link',
    '--color-hero-content-bg',
  ],
  highlightTextColour: [
    '--color-button-primary-text',
    '--color-hero-content-heading',
  ],
  secondaryButtonColour: ['--color-button-secondary-bg'],
  secondaryButtonBorderColour: ['--color-button-secondary-border'],
  secondaryButtonTextColour: ['--color-button-secondary-text'],
  tertiaryButtonColour: ['--color-button-tertiary-bg'],
  tertiaryButtonBorderColour: ['--color-button-tertiary-border'],
  tertiaryButtonTextColour: ['--color-button-tertiary-text'],
  navBackgroundColour: ['--color-nav-bg'],
  modalBackgroundColour: [
    '--color-modal-bg',
    '--color-background-tertiary',
  ],
  inputBackgroundColour: ['--color-input-bg'],
  inputBorderColour: ['--color-input-border'],
  inputTextColour: ['--color-input-text'],
};

export const useTheme = () => {
  const { data: settings } = useGetSettingsQuery();
  const design = settings?.galleryDesign;
  const designLight = settings?.galleryDesignLight;
  const separatePalette = settings?.separateLightDarkPalette;

  useEffect(() => {
    if (!design) return;

    const root = document.documentElement.style;

    if (separatePalette && designLight) {
      document.documentElement.style.setProperty('color-scheme', 'light dark');

      for (const [key, cssVars] of Object.entries(themeMap)) {
        const darkValue = design[key as keyof typeof themeMap];
        const lightValue = designLight[key as keyof typeof themeMap];
        if (darkValue && lightValue) {
          for (const cssVar of cssVars) {
            root.setProperty(cssVar, `light-dark(${lightValue}, ${darkValue})`);
          }
        } else if (darkValue) {
          for (const cssVar of cssVars) {
            root.setProperty(cssVar, darkValue);
          }
        }
      }

      const darkFont = design.font ? resolveFont(design.font) : null;
      const lightFont = designLight.font
        ? resolveFont(designLight.font)
        : null;
      if (darkFont) {
        root.setProperty('--font-family', darkFont);
      }
      if (lightFont && lightFont !== darkFont) {
        root.setProperty('--font-family-light', lightFont);
      } else {
        root.removeProperty('--font-family-light');
      }
    } else {
      document.documentElement.style.removeProperty('color-scheme');
      root.removeProperty('--font-family-light');

      for (const [key, cssVars] of Object.entries(themeMap)) {
        const value = design[key as keyof typeof themeMap];
        if (value) {
          for (const cssVar of cssVars) {
            root.setProperty(cssVar, value);
          }
        }
      }

      if (design.font) {
        root.setProperty('--font-family', resolveFont(design.font));
      }
    }
  }, [design, designLight, separatePalette]);
};
